import type {
  LoginRequest,
  RegisterRequest,
  VerifyEmailRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthResponse,
  MessageResponse,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationResponse,
  OrganizationDetailResponse,
  AddMemberRequest,
  UpdateMemberRoleRequest,
  MemberResponse,
  CreateTeamRequest,
  UpdateTeamRequest,
  TeamResponse,
  AddTeamMemberRequest,
  TeamMemberResponse,
  ErrorResponse,
  BillingStatusResponse,
  CreateCheckoutRequest,
  CheckoutResponse,
  CreatePortalRequest,
  PortalResponse,
  InvoicesResponse,
  PodcastResponse,
  CreatePodcastRequest,
  UpdatePodcastRequest,
  EpisodeResponse,
  CreateEpisodeRequest,
  UpdateEpisodeRequest,
  ApiKeyResponse,
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
  CustomDomainResponse,
  DomainWithInstructions,
  VerificationResult,
  Transcript,
  TranscriptResponse,
  TranscriptSaveResponse,
} from "../types/api";

// API Error class
export class APIError extends Error {
  statusCode: number;
  data?: unknown;

  constructor(message: string, statusCode: number, data?: unknown) {
    super(message);
    this.name = "APIError";
    this.statusCode = statusCode;
    this.data = data;
  }
}

// Base URL configuration
const getBaseURL = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Default to /api which will be proxied to backend in development
  return "/api";
};

// Generic fetch wrapper with error handling
// (touch commit to force Cloudflare Pages frontend redeploy)
// (second touch: ensure frontend path-filter triggers Deploy workflow)
async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const baseURL = getBaseURL();
  const url = `${baseURL}${endpoint}`;

  // Always include credentials for cookie-based sessions
  const config: RequestInit = {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Parse response body
    let data: unknown;
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Handle error responses
    if (!response.ok) {
      const errorMessage =
        typeof data === "object" && data !== null && "error" in data
          ? (data as ErrorResponse).error
          : typeof data === "string"
            ? data
            : `Request failed with status ${response.status}`;

      throw new APIError(errorMessage, response.status, data);
    }

    return data as T;
  } catch (error) {
    // Re-throw APIError as-is
    if (error instanceof APIError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      throw new APIError("Network error: Unable to connect to server", 0, error);
    }

    // Handle other errors
    throw new APIError(error instanceof Error ? error.message : "Unknown error occurred", 0, error);
  }
}

// Auth API functions
export const prereleaseAPI = {
  getConfig: (): Promise<{ prereleaseMode: boolean }> =>
    fetchAPI<{ prereleaseMode: boolean }>("/config/prerelease"),

  joinWaitlist: (email: string): Promise<{ message: string }> =>
    fetchAPI<{ message: string }>("/waitlist", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
};

export const authAPI = {
  login: (data: LoginRequest): Promise<AuthResponse> =>
    fetchAPI<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  register: (data: RegisterRequest, bypass?: string): Promise<AuthResponse> => {
    const url = bypass ? `/auth/register?bypass=${encodeURIComponent(bypass)}` : "/auth/register";
    return fetchAPI<AuthResponse>(url, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  logout: (): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/logout", {
      method: "POST",
    }),

  me: (): Promise<AuthResponse> => fetchAPI<AuthResponse>("/auth/me"),

  verifyEmail: (data: VerifyEmailRequest): Promise<AuthResponse> =>
    fetchAPI<AuthResponse>("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  resendVerification: (): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/resend-verification", {
      method: "POST",
    }),

  forgotPassword: (data: ForgotPasswordRequest): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  resetPassword: (data: ResetPasswordRequest): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// Organization API functions
export const organizationAPI = {
  getAll: async (): Promise<OrganizationResponse[]> => {
    const response = await fetchAPI<{ organizations: OrganizationResponse[] }>("/organizations");
    return response.organizations;
  },

  getById: async (id: string): Promise<OrganizationDetailResponse> => {
    const response = await fetchAPI<OrganizationDetailResponse>(`/organizations/${id}`);
    return response;
  },

  create: async (data: CreateOrganizationRequest): Promise<OrganizationResponse> => {
    const response = await fetchAPI<{ organization: OrganizationResponse }>("/organizations", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.organization;
  },

  update: async (id: string, data: UpdateOrganizationRequest): Promise<OrganizationResponse> => {
    const response = await fetchAPI<{ organization: OrganizationResponse }>(
      `/organizations/${id}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.organization;
  },

  delete: (id: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${id}`, {
      method: "DELETE",
    }),

  getMembers: async (id: string): Promise<MemberResponse[]> => {
    const response = await fetchAPI<{ members: MemberResponse[] }>(`/organizations/${id}/members`);
    return response.members;
  },

  addMember: async (id: string, data: AddMemberRequest): Promise<MemberResponse> => {
    const response = await fetchAPI<{ member: MemberResponse }>(`/organizations/${id}/members`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.member;
  },

  updateMemberRole: async (
    id: string,
    userId: string,
    data: UpdateMemberRoleRequest,
  ): Promise<MemberResponse> => {
    const response = await fetchAPI<{ member: MemberResponse }>(
      `/organizations/${id}/members/${userId}/role`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.member;
  },

  removeMember: (id: string, userId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${id}/members/${userId}`, {
      method: "DELETE",
    }),

  // API Key methods
  getApiKeys: async (id: string): Promise<ApiKeyResponse[]> => {
    const response = await fetchAPI<{ apiKeys: ApiKeyResponse[] }>(`/organizations/${id}/api-keys`);
    return response.apiKeys;
  },

  createApiKey: async (
    id: string,
    data: CreateApiKeyRequest,
  ): Promise<{ apiKey: ApiKeyResponse; rawKey: string }> => {
    const response = await fetchAPI<{ apiKey: ApiKeyResponse; rawKey: string }>(
      `/organizations/${id}/api-keys`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return response;
  },

  updateApiKey: async (
    id: string,
    keyId: string,
    data: UpdateApiKeyRequest,
  ): Promise<ApiKeyResponse> => {
    const response = await fetchAPI<{ apiKey: ApiKeyResponse }>(
      `/organizations/${id}/api-keys/${keyId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.apiKey;
  },

  revokeApiKey: async (id: string, keyId: string): Promise<ApiKeyResponse> => {
    const response = await fetchAPI<{ apiKey: ApiKeyResponse }>(
      `/organizations/${id}/api-keys/${keyId}/revoke`,
      {
        method: "POST",
      },
    );
    return response.apiKey;
  },

  rotateApiKey: async (
    id: string,
    keyId: string,
  ): Promise<{ apiKey: ApiKeyResponse; rawKey: string }> => {
    const response = await fetchAPI<{ apiKey: ApiKeyResponse; rawKey: string }>(
      `/organizations/${id}/api-keys/${keyId}/rotate`,
      {
        method: "POST",
      },
    );
    return response;
  },
};

// Team API functions
export const teamAPI = {
  getAll: async (orgId: string): Promise<TeamResponse[]> => {
    const response = await fetchAPI<{ teams: TeamResponse[] }>(`/organizations/${orgId}/teams`);
    return response.teams;
  },

  getById: async (orgId: string, teamId: string): Promise<TeamResponse> => {
    const response = await fetchAPI<{ team: TeamResponse }>(
      `/organizations/${orgId}/teams/${teamId}`,
    );
    return response.team;
  },

  create: async (orgId: string, data: CreateTeamRequest): Promise<TeamResponse> => {
    const response = await fetchAPI<{ team: TeamResponse }>(`/organizations/${orgId}/teams`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.team;
  },

  update: async (orgId: string, teamId: string, data: UpdateTeamRequest): Promise<TeamResponse> => {
    const response = await fetchAPI<{ team: TeamResponse }>(
      `/organizations/${orgId}/teams/${teamId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.team;
  },

  delete: (orgId: string, teamId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${orgId}/teams/${teamId}`, {
      method: "DELETE",
    }),

  getMembers: async (orgId: string, teamId: string): Promise<TeamMemberResponse[]> => {
    const response = await fetchAPI<{ members: TeamMemberResponse[] }>(
      `/organizations/${orgId}/teams/${teamId}/members`,
    );
    return response.members;
  },

  addMember: async (
    orgId: string,
    teamId: string,
    data: AddTeamMemberRequest,
  ): Promise<TeamMemberResponse> => {
    const response = await fetchAPI<{ member: TeamMemberResponse }>(
      `/organizations/${orgId}/teams/${teamId}/members`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return response.member;
  },

  removeMember: (orgId: string, teamId: string, userId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${orgId}/teams/${teamId}/members/${userId}`, {
      method: "DELETE",
    }),
};

// Billing API functions
export const billingAPI = {
  getStatus: (orgId: string): Promise<BillingStatusResponse> =>
    fetchAPI<BillingStatusResponse>(`/organizations/${orgId}/billing`),

  createCheckout: (orgId: string, data: CreateCheckoutRequest): Promise<CheckoutResponse> =>
    fetchAPI<CheckoutResponse>(`/organizations/${orgId}/billing/checkout`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  createPortal: (orgId: string, data: CreatePortalRequest): Promise<PortalResponse> =>
    fetchAPI<PortalResponse>(`/organizations/${orgId}/billing/portal`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  cancel: (orgId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${orgId}/billing/cancel`, {
      method: "POST",
    }),

  getInvoices: (orgId: string): Promise<InvoicesResponse> =>
    fetchAPI<InvoicesResponse>(`/organizations/${orgId}/billing/invoices`),
};

export interface StripeConnectStatus {
  connected: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  stripeAccountId: string | null;
  disconnectedAt: string | null;
}

export interface StripeConnectAuthorizeResult {
  url: string;
}

export const stripeConnectAPI = {
  getStatus: (orgId: string): Promise<StripeConnectStatus> =>
    fetchAPI<StripeConnectStatus>(`/organizations/${orgId}/stripe-connect/status`),

  authorize: (
    orgId: string,
    returnUrl: string,
    refreshUrl: string,
  ): Promise<StripeConnectAuthorizeResult> =>
    fetchAPI<StripeConnectAuthorizeResult>(`/organizations/${orgId}/stripe-connect/authorize`, {
      method: "POST",
      body: JSON.stringify({ returnUrl, refreshUrl }),
    }),

  disconnect: (orgId: string): Promise<{ disconnected: boolean }> =>
    fetchAPI<{ disconnected: boolean }>(`/organizations/${orgId}/stripe-connect/disconnect`, {
      method: "POST",
    }),
};

// Podcast API functions
export const podcastAPI = {
  getAll: async (orgId: string): Promise<PodcastResponse[]> => {
    const response = await fetchAPI<{ podcasts: PodcastResponse[] }>(
      `/organizations/${orgId}/podcasts`,
    );
    return response.podcasts;
  },

  getById: async (orgId: string, podcastId: string): Promise<PodcastResponse> => {
    const response = await fetchAPI<{ podcast: PodcastResponse }>(
      `/organizations/${orgId}/podcasts/${podcastId}`,
    );
    return response.podcast;
  },

  create: async (orgId: string, data: CreatePodcastRequest): Promise<PodcastResponse> => {
    const response = await fetchAPI<{ podcast: PodcastResponse }>(
      `/organizations/${orgId}/podcasts`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return response.podcast;
  },

  update: async (
    orgId: string,
    podcastId: string,
    data: UpdatePodcastRequest,
  ): Promise<PodcastResponse> => {
    const response = await fetchAPI<{ podcast: PodcastResponse }>(
      `/organizations/${orgId}/podcasts/${podcastId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.podcast;
  },

  delete: (orgId: string, podcastId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(`/organizations/${orgId}/podcasts/${podcastId}`, {
      method: "DELETE",
    }),
};

// Episode API functions
export const episodeAPI = {
  getAll: async (
    orgId: string,
    podcastId: string,
    options?: { status?: string; limit?: number; offset?: number },
  ): Promise<EpisodeResponse[]> => {
    const params = new URLSearchParams();
    if (options?.status) params.append("status", options.status);
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());
    const query = params.toString() ? `?${params.toString()}` : "";

    const response = await fetchAPI<{ episodes: EpisodeResponse[] }>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes${query}`,
    );
    return response.episodes;
  },

  getById: async (
    orgId: string,
    podcastId: string,
    episodeId: string,
  ): Promise<EpisodeResponse> => {
    const response = await fetchAPI<{ episode: EpisodeResponse }>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}`,
    );
    return response.episode;
  },

  create: async (
    orgId: string,
    podcastId: string,
    data: CreateEpisodeRequest,
  ): Promise<EpisodeResponse> => {
    const response = await fetchAPI<{ episode: EpisodeResponse }>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return response.episode;
  },

  update: async (
    orgId: string,
    podcastId: string,
    episodeId: string,
    data: UpdateEpisodeRequest,
  ): Promise<EpisodeResponse> => {
    const response = await fetchAPI<{ episode: EpisodeResponse }>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return response.episode;
  },

  delete: (orgId: string, podcastId: string, episodeId: string): Promise<MessageResponse> =>
    fetchAPI<MessageResponse>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}`,
      { method: "DELETE" },
    ),

  publish: async (
    orgId: string,
    podcastId: string,
    episodeId: string,
  ): Promise<EpisodeResponse> => {
    const response = await fetchAPI<{ episode: EpisodeResponse }>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}/publish`,
      { method: "POST" },
    );
    return response.episode;
  },
};

// Custom Domains API
export const domainAPI = {
  list: async (orgId: string, podcastId: string): Promise<CustomDomainResponse[]> => {
    const response = await fetchAPI<{ domains: CustomDomainResponse[] }>(
      `/organizations/${orgId}/podcasts/${podcastId}/domains`,
    );
    return response.domains;
  },

  add: async (orgId: string, podcastId: string, domain: string): Promise<DomainWithInstructions> =>
    fetchAPI<DomainWithInstructions>(`/organizations/${orgId}/podcasts/${podcastId}/domains`, {
      method: "POST",
      body: JSON.stringify({ domain }),
    }),

  get: async (
    orgId: string,
    podcastId: string,
    domainId: string,
  ): Promise<DomainWithInstructions> =>
    fetchAPI<DomainWithInstructions>(
      `/organizations/${orgId}/podcasts/${podcastId}/domains/${domainId}`,
    ),

  verify: async (orgId: string, podcastId: string, domainId: string): Promise<VerificationResult> =>
    fetchAPI<VerificationResult>(
      `/organizations/${orgId}/podcasts/${podcastId}/domains/${domainId}/verify`,
      { method: "POST" },
    ),

  remove: (orgId: string, podcastId: string, domainId: string): Promise<void> =>
    fetchAPI<void>(`/organizations/${orgId}/podcasts/${podcastId}/domains/${domainId}`, {
      method: "DELETE",
    }),
};

import type {
  AudioProcessingStepType,
  StartProcessingResponse,
  ListProcessingJobsResponse,
  AudioProcessingJobDetail,
} from "../types/api";

export const audioProcessingAPI = {
  start: (
    orgId: string,
    podcastId: string,
    episodeId: string,
    steps: AudioProcessingStepType[],
  ): Promise<StartProcessingResponse> =>
    fetchAPI<StartProcessingResponse>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}/process`,
      { method: "POST", body: JSON.stringify({ steps }) },
    ),

  list: async (
    orgId: string,
    podcastId: string,
    episodeId: string,
  ): Promise<ListProcessingJobsResponse> =>
    fetchAPI<ListProcessingJobsResponse>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}/process`,
    ),

  getJob: (
    orgId: string,
    podcastId: string,
    episodeId: string,
    jobId: string,
  ): Promise<AudioProcessingJobDetail> =>
    fetchAPI<AudioProcessingJobDetail>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}/process/${jobId}`,
    ),
};

import type {
  PodcastStats,
  EpisodeStats,
  GeoEntry,
  DeviceEntry,
  GrowthPoint,
} from "../types/analytics";

function dateParams(from: Date, to: Date): string {
  return `?from=${from.toISOString()}&to=${to.toISOString()}`;
}

export const analyticsAPI = {
  overview: (orgId: string, podcastId: string, from: Date, to: Date): Promise<PodcastStats> =>
    fetchAPI<PodcastStats>(
      `/organizations/${orgId}/podcasts/${podcastId}/analytics/overview${dateParams(from, to)}`,
    ),

  episode: (
    orgId: string,
    podcastId: string,
    episodeId: string,
    from: Date,
    to: Date,
  ): Promise<EpisodeStats> =>
    fetchAPI<EpisodeStats>(
      `/organizations/${orgId}/podcasts/${podcastId}/analytics/episodes/${episodeId}${dateParams(from, to)}`,
    ),

  geo: (orgId: string, podcastId: string, from: Date, to: Date): Promise<GeoEntry[]> =>
    fetchAPI<GeoEntry[]>(
      `/organizations/${orgId}/podcasts/${podcastId}/analytics/geo${dateParams(from, to)}`,
    ),

  devices: (orgId: string, podcastId: string, from: Date, to: Date): Promise<DeviceEntry[]> =>
    fetchAPI<DeviceEntry[]>(
      `/organizations/${orgId}/podcasts/${podcastId}/analytics/devices${dateParams(from, to)}`,
    ),

  growth: (orgId: string, podcastId: string, from: Date, to: Date): Promise<GrowthPoint[]> =>
    fetchAPI<GrowthPoint[]>(
      `/organizations/${orgId}/podcasts/${podcastId}/analytics/growth${dateParams(from, to)}`,
    ),
};

export interface TeamPodcastScope {
  id: string;
  team_id: string;
  podcast_id: string;
  permissions: string[];
  created_at: string;
  updated_at: string;
}

export const teamPodcastScopeAPI = {
  list: async (orgId: string, podcastId: string): Promise<TeamPodcastScope[]> => {
    const response = await fetchAPI<{ scopes: TeamPodcastScope[] }>(
      `/organizations/${orgId}/podcasts/${podcastId}/teams`,
    );
    return response.scopes;
  },

  assign: (
    orgId: string,
    podcastId: string,
    teamId: string,
    permissions: string[],
  ): Promise<TeamPodcastScope> =>
    fetchAPI<TeamPodcastScope>(
      `/organizations/${orgId}/podcasts/${podcastId}/teams/${teamId}/permissions`,
      { method: "POST", body: JSON.stringify({ permissions }) },
    ),

  update: (
    orgId: string,
    podcastId: string,
    teamId: string,
    permissions: string[],
  ): Promise<TeamPodcastScope> =>
    fetchAPI<TeamPodcastScope>(
      `/organizations/${orgId}/podcasts/${podcastId}/teams/${teamId}/permissions`,
      { method: "PATCH", body: JSON.stringify({ permissions }) },
    ),

  remove: (orgId: string, podcastId: string, teamId: string): Promise<void> =>
    fetchAPI<void>(`/organizations/${orgId}/podcasts/${podcastId}/teams/${teamId}/permissions`, {
      method: "DELETE",
    }),
};

// Transcript API
export const transcriptAPI = {
  get: (orgId: string, podcastId: string, episodeId: string): Promise<TranscriptResponse> =>
    fetchAPI<TranscriptResponse>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}/transcript`,
    ),

  save: (
    orgId: string,
    podcastId: string,
    episodeId: string,
    transcript: Transcript,
  ): Promise<TranscriptSaveResponse> =>
    fetchAPI<TranscriptSaveResponse>(
      `/organizations/${orgId}/podcasts/${podcastId}/episodes/${episodeId}/transcript`,
      { method: "PUT", body: JSON.stringify({ transcript }) },
    ),

  exportUrl: (episodeId: string, format: "srt" | "vtt"): string =>
    `/public/episodes/${episodeId}/transcript.${format}`,
};

// Network Revenue Analytics API
import type { RevenueSummary, MonthlyRevenueReport } from "../types/networkAnalytics";

export const networkRevenueAPI = {
  getSummary: (orgId: string, networkId: string): Promise<RevenueSummary> =>
    fetchAPI<RevenueSummary>(`/organizations/${orgId}/networks/${networkId}/revenue`),

  getMonth: (orgId: string, networkId: string, month: string): Promise<MonthlyRevenueReport> =>
    fetchAPI<MonthlyRevenueReport>(
      `/organizations/${orgId}/networks/${networkId}/revenue/${month}`,
    ),
};

// Public API (no auth required)
export const publicAPI = {
  getPodcast: (slug: string) =>
    fetchAPI<{
      podcast: {
        id: string;
        name: string;
        slug: string;
        description: string;
        coverArtworkUrl: string | null;
        author: string | null;
        language: string | null;
        category: string | null;
      };
      pricing: Array<{
        id: string;
        podcastId: string;
        interval: "monthly" | "yearly";
        priceCents: number;
        trialDays: number;
        active: boolean;
      }>;
    }>(`/public/podcasts/${slug}`),
};

// Listener Subscription API (public checkout)
export const listenerSubscriptionAPI = {
  checkout: (data: {
    podcastId: string;
    email: string;
    interval: "monthly" | "yearly";
    successUrl: string;
    cancelUrl: string;
  }) =>
    fetchAPI<{ url: string }>("/listener-subscriptions/checkout", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// Subscriber API
export interface SubscriberResponse {
  id: string;
  podcastId: string;
  email: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  feedAccessCount: number;
  lastFeedAccess: string | null;
  createdAt: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
}

export interface SubscriberStatsResponse {
  totalActive: number;
  totalCancelled: number;
  totalExpired: number;
  totalRevoked: number;
  mrr: number;
}

export const subscriberAPI = {
  list: async (
    orgId: string,
    podcastId: string,
    params?: { status?: string; limit?: number; offset?: number },
  ): Promise<{ subscribers: SubscriberResponse[]; total: number }> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append("status", params.status);
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.offset) searchParams.append("offset", params.offset.toString());
    const query = searchParams.toString() ? `?${searchParams.toString()}` : "";
    return fetchAPI<{ subscribers: SubscriberResponse[]; total: number }>(
      `/organizations/${orgId}/podcasts/${podcastId}/subscribers${query}`,
    );
  },

  getStats: (orgId: string, podcastId: string): Promise<SubscriberStatsResponse> =>
    fetchAPI<SubscriberStatsResponse>(
      `/organizations/${orgId}/podcasts/${podcastId}/subscribers/stats`,
    ),
};

export function exportSubscribersCSV(subscribers: SubscriberResponse[]): void {
  const headers = [
    "Email",
    "Status",
    "Period Start",
    "Period End",
    "Feed Access Count",
    "Last Access",
    "Created At",
  ];
  const rows = subscribers.map((s) => [
    s.email,
    s.status,
    s.currentPeriodStart ?? "",
    s.currentPeriodEnd ?? "",
    s.feedAccessCount.toString(),
    s.lastFeedAccess ?? "",
    s.createdAt,
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "subscribers.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// Network API
import type {
  NetworkResponse,
  CreateNetworkRequest,
  UpdateNetworkRequest,
  NetworkTierResponse,
  CreateNetworkTierRequest,
  UpdateNetworkTierRequest,
  NetworkMembershipResponse,
  InvitePodcastRequest,
  UpdateMembershipRequest,
} from "../types/api";

export const networkAPI = {
  getAll: async (orgId: string): Promise<NetworkResponse[]> => {
    const response = await fetchAPI<NetworkResponse[]>(`/organizations/${orgId}/networks`);
    return response;
  },

  getById: async (orgId: string, networkId: string): Promise<NetworkResponse> => {
    const response = await fetchAPI<NetworkResponse>(
      `/organizations/${orgId}/networks/${networkId}`,
    );
    return response;
  },

  create: async (orgId: string, data: CreateNetworkRequest): Promise<NetworkResponse> => {
    const response = await fetchAPI<NetworkResponse>(`/organizations/${orgId}/networks`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response;
  },

  update: async (
    orgId: string,
    networkId: string,
    data: UpdateNetworkRequest,
  ): Promise<NetworkResponse> => {
    const response = await fetchAPI<NetworkResponse>(
      `/organizations/${orgId}/networks/${networkId}`,
      { method: "PATCH", body: JSON.stringify(data) },
    );
    return response;
  },

  delete: (orgId: string, networkId: string): Promise<void> =>
    fetchAPI<void>(`/organizations/${orgId}/networks/${networkId}`, {
      method: "DELETE",
    }),
};

// Network Tier API
export const tierAPI = {
  getAll: async (orgId: string, networkId: string): Promise<NetworkTierResponse[]> => {
    const response = await fetchAPI<NetworkTierResponse[]>(
      `/organizations/${orgId}/networks/${networkId}/tiers`,
    );
    return response;
  },

  getById: async (
    orgId: string,
    networkId: string,
    tierId: string,
  ): Promise<NetworkTierResponse> => {
    const response = await fetchAPI<NetworkTierResponse>(
      `/organizations/${orgId}/networks/${networkId}/tiers/${tierId}`,
    );
    return response;
  },

  create: async (
    orgId: string,
    networkId: string,
    data: CreateNetworkTierRequest,
  ): Promise<NetworkTierResponse> => {
    const response = await fetchAPI<NetworkTierResponse>(
      `/organizations/${orgId}/networks/${networkId}/tiers`,
      { method: "POST", body: JSON.stringify(data) },
    );
    return response;
  },

  update: async (
    orgId: string,
    networkId: string,
    tierId: string,
    data: UpdateNetworkTierRequest,
  ): Promise<NetworkTierResponse> => {
    const response = await fetchAPI<NetworkTierResponse>(
      `/organizations/${orgId}/networks/${networkId}/tiers/${tierId}`,
      { method: "PATCH", body: JSON.stringify(data) },
    );
    return response;
  },

  delete: (orgId: string, networkId: string, tierId: string): Promise<void> =>
    fetchAPI<void>(`/organizations/${orgId}/networks/${networkId}/tiers/${tierId}`, {
      method: "DELETE",
    }),

  reorder: async (
    orgId: string,
    networkId: string,
    tierIds: string[],
  ): Promise<NetworkTierResponse[]> => {
    const response = await fetchAPI<NetworkTierResponse[]>(
      `/organizations/${orgId}/networks/${networkId}/tiers/reorder`,
      { method: "POST", body: JSON.stringify({ tierIds }) },
    );
    return response;
  },
};

// Network Membership API
export const membershipAPI = {
  getAll: async (orgId: string, networkId: string): Promise<NetworkMembershipResponse[]> => {
    const response = await fetchAPI<NetworkMembershipResponse[]>(
      `/organizations/${orgId}/networks/${networkId}/memberships`,
    );
    return response;
  },

  invite: async (
    orgId: string,
    networkId: string,
    data: InvitePodcastRequest,
  ): Promise<NetworkMembershipResponse> => {
    const response = await fetchAPI<NetworkMembershipResponse>(
      `/organizations/${orgId}/networks/${networkId}/invite`,
      { method: "POST", body: JSON.stringify(data) },
    );
    return response;
  },

  accept: async (
    orgId: string,
    networkId: string,
    membershipId: string,
  ): Promise<NetworkMembershipResponse> => {
    const response = await fetchAPI<NetworkMembershipResponse>(
      `/organizations/${orgId}/networks/${networkId}/memberships/${membershipId}/accept`,
      { method: "POST" },
    );
    return response;
  },

  reject: async (
    orgId: string,
    networkId: string,
    membershipId: string,
  ): Promise<NetworkMembershipResponse> => {
    const response = await fetchAPI<NetworkMembershipResponse>(
      `/organizations/${orgId}/networks/${networkId}/memberships/${membershipId}/reject`,
      { method: "POST" },
    );
    return response;
  },

  leave: async (
    orgId: string,
    networkId: string,
    membershipId: string,
    podcastId: string,
  ): Promise<NetworkMembershipResponse> => {
    const response = await fetchAPI<NetworkMembershipResponse>(
      `/organizations/${orgId}/networks/${networkId}/memberships/${membershipId}/leave`,
      { method: "POST", body: JSON.stringify({ podcastId }) },
    );
    return response;
  },

  updateSplit: async (
    orgId: string,
    networkId: string,
    membershipId: string,
    data: UpdateMembershipRequest,
  ): Promise<NetworkMembershipResponse> => {
    const response = await fetchAPI<NetworkMembershipResponse>(
      `/organizations/${orgId}/networks/${networkId}/memberships/${membershipId}`,
      { method: "PATCH", body: JSON.stringify(data) },
    );
    return response;
  },
};
